import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Bits";
import { hhmm, datetime, time, date } from "@/lib/format";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, ShieldCheck, Pencil } from "lucide-react";
import { SuperEdit } from "@/components/SuperEdit";
import { SuperDelete } from "@/components/SuperDelete";

export default function TimeLabour() {
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Time & Labour" subtitle="Attendance, job time and unallocated hours" />
      <Tabs defaultValue="unallocated">
        <TabsList data-testid="time-tabs">
          <TabsTrigger value="unallocated" data-testid="tab-unallocated">Unallocated</TabsTrigger>
          <TabsTrigger value="job-time" data-testid="tab-jobtime">Job Time</TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">Attendance</TabsTrigger>
        </TabsList>
        <TabsContent value="unallocated"><Unallocated /></TabsContent>
        <TabsContent value="job-time"><JobTime /></TabsContent>
        <TabsContent value="attendance"><Attendance /></TabsContent>
      </Tabs>
    </div>
  );
}

function Unallocated() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/unallocated-time").then((r) => setRows(r.data)); }, []);
  if (rows.length === 0) return <Empty text="No completed shifts to reconcile." />;
  return (
    <div className="card-industrial overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Date</th>
          <th className="text-right px-4 py-3">Worked</th><th className="text-right px-4 py-3">Booked</th><th className="text-right px-4 py-3">Unallocated</th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r) => (
            <tr key={r.attendance_id} data-testid={`unalloc-${r.attendance_id}`}>
              <td className="px-4 py-3 font-semibold">{r.user_name}</td>
              <td className="px-4 py-3 text-muted-foreground">{date(r.date)}</td>
              <td className="px-4 py-3 text-right font-mono-num">{hhmm(r.worked_minutes)}</td>
              <td className="px-4 py-3 text-right font-mono-num">{hhmm(r.booked_minutes)}</td>
              <td className={`px-4 py-3 text-right font-mono-num font-bold ${r.unallocated_minutes > 0 ? "text-amber-400" : "text-emerald-400"}`}>{hhmm(r.unallocated_minutes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function JobTime() {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [minutes, setMinutes] = useState(0);
  const [reason, setReason] = useState("");

  const load = useCallback(() => { api.get("/job-time").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.patch(`/job-time/${edit.id}`, { minutes: Number(minutes), reason });
      toast.success("Corrected (logged to audit)");
      setEdit(null); setReason(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (rows.length === 0) return <Empty text="No job time booked." />;
  return (
    <>
      <div className="card-industrial overflow-hidden mt-4">
        <table className="w-full text-sm">
          <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Customer / Job</th><th className="text-left px-4 py-3">Task</th><th className="text-left px-4 py-3">Date</th>
            <th className="text-right px-4 py-3">Time</th><th className="text-left px-4 py-3">Note</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.map((t) => (
              <tr key={t.id} data-testid={`jobtime-${t.id}`}>
                <td className="px-4 py-3 max-w-[220px]"><div className="text-[11px] text-muted-foreground truncate">{t.customer_name}</div><div className="font-semibold truncate">{t.job_name || t.job_number}</div>{t.user_name && <div className="text-[11px] text-muted-foreground">{t.user_name}</div>}</td>
                <td className="px-4 py-3">{t.stage_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{date(t.date)}{t.start_time && <div className="text-xs font-mono-num" data-testid={`jobtime-times-${t.id}`}>{t.start_time}–{t.finish_time}</div>}</td>
                <td className="px-4 py-3 text-right font-mono-num">{hhmm(t.minutes)}{t.corrected && <span className="ml-1 text-[10px] text-amber-400">✎</span>}</td>
                <td className="px-4 py-3 text-muted-foreground truncate max-w-[160px]">{t.note}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                  <button onClick={() => { setEdit(t); setMinutes(t.minutes); }} data-testid={`correct-${t.id}`} className="text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                  <SuperEdit entity="job_time" record={t} label="time entry" onDone={load} /><SuperDelete entity="job_time" id={t.id} label={`time entry on ${t.job_number}`} onDone={load} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading uppercase">Correct Time Entry</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-muted-foreground">Minutes</label><Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="correct-minutes" /></div>
            <div><label className="text-xs text-muted-foreground">Reason (audited)</label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="correct-reason" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="correct-save" className="font-bold uppercase">Save Correction</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Attendance() {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/attendance").then((r) => setRows(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (rows.length === 0) return <Empty text="No attendance records." />;
  return (
    <div className="card-industrial overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
          <tr><th className="text-left px-4 py-3">Employee</th><th className="text-left px-4 py-3">Site</th><th className="text-left px-4 py-3">In</th>
          <th className="text-left px-4 py-3">Out</th><th className="text-left px-4 py-3">GPS</th><th className="px-4 py-3"></th></tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((a) => (
            <tr key={a.id} data-testid={`attendance-${a.id}`} className={a.flagged ? "bg-red-500/5" : ""}>
              <td className="px-4 py-3 font-semibold">{a.user_name}</td>
              <td className="px-4 py-3 text-muted-foreground">{a.location}{a.off_site_note && <div className="text-xs text-amber-400" data-testid={`shift-offsite-note-${a.id}`}>{a.off_site_note}</div>}</td>
              <td className="px-4 py-3 font-mono-num">{datetime(a.clock_in_at)}</td>
              <td className="px-4 py-3 font-mono-num">{a.clock_out_at ? time(a.clock_out_at) : <span className="text-emerald-400">active</span>}</td>
              <td className="px-4 py-3">
                {a.geofence_ok ? (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><ShieldCheck className="w-3.5 h-3.5" />{a.distance_m}m</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-red-400" title={a.flag_reason}><ShieldAlert className="w-3.5 h-3.5" />{a.distance_m ?? "?"}m</span>
                )}
              </td>
              <td className="px-4 py-3 text-right whitespace-nowrap"><div className="flex justify-end gap-2">
                <SuperEdit entity="attendance" record={a} label="shift" onDone={load} /><SuperDelete entity="attendance" id={a.id} label={`shift for ${a.user_name}`} onDone={load} />
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
